import { Fragment, useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  confirmCatalogImportWithProgress,
  discardCatalogImportPreviewSession,
  getCatalogImportPreviewSession,
  importCatalogPreview,
  importCatalogPreviewRows,
  saveCatalogImportPreview,
  type CatalogImportTarget,
  type ImportJobProgress,
} from '../api/catalogImport'
import { ImportProgress } from '../components/import/ImportProgress'
import { pluralRu } from '../components/myPrices/pluralRu'
import {
  CATALOG_IMPORT_FIELDS,
  catalogImportFieldLabel,
  columnMappingFromPreview,
  importMatchDecisionLabel,
  mappingSourceLabel,
  type CatalogImportConfirmResultDto,
  type CatalogImportPreviewDto,
  type CatalogImportPreviewRowsDto,
  type CatalogImportRowFieldOverride,
  type CatalogImportField,
  type ColumnMapping,
  type ExistingItemUpdateMode,
  type ImportPreviewRowDto,
} from '../api/catalogImportTypes'
import { listProductTypes, mergeProductTypes, resolveProductTypes, type ProductTypeDto } from '../api/productTypes'
import { listSupplierCatalogFilterOptions } from '../api/supplierCatalog'
import { listRestaurantMyPricesFilterOptions, listRestaurantPrivateSuppliers, type RestaurantPrivateSupplierDto } from '../api/restaurantMyPrices'
import { useAuth } from '../auth/AuthProvider'
import { useActiveVenue } from '../venues/ActiveVenueProvider'
import { useTableColumnPrefs, type TableColumnDef } from '../hooks/useTableColumnPrefs'
import { ogrnLabel, ogrnValidationMessage } from '../utils/ogrn'
import {
  ImportRowEditPanel,
  effectiveImportRowTypeId,
  buildConfirmProductTypeOverrides,
  buildConfirmMatcherSuggestions,
  importRowTypeLabel,
} from '../components/ImportRowEditPanel'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketLoadingIndicator } from '../components/MarketLoadingIndicator'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { ImportSupplierConfirmModal } from '../components/ImportSupplierConfirmModal'
import { ResizableTh } from '../components/ResizableTh'
import { PreviewPageJump } from '../components/PreviewPageJump'
import { SelectCombobox } from '../components/SelectCombobox'
import { ImportStepper } from '../components/ImportStepper'
import { TableColumnSettings } from '../components/TableColumnSettings'
import { IconPencil, IconTrash, IconUndo } from '../components/icons/CatalogActionIcons'
import { operatorBuyerPricesPath, operatorSupplierCatalogPath } from '../operator/paths'
import { formatApiErrorMessage } from '../api/apiErrorMessage'
import { billingErrorMessage, parseBillingError } from '../api/apiErrorCodes'
import { ApiError } from '../api/client'

import { PaywallModal } from '../components/billing/PaywallModal'
import {
  clearCatalogImportDraft,
  draftFromServerSession,
  formatDraftExpiry,
  loadCatalogImportDraft,
  saveCatalogImportDraft,
  type CatalogImportDraft,
} from '../storage/catalogImportDraft'
import {
  IMPORT_SESSION_EXPIRED_MESSAGE,
  isImportPreviewSessionApiError,
  isPreviewSessionExpired,
  previewSessionMinutesLeft,
} from '../utils/catalogImportSession'

// Keep pages small: preview rematches each row; large pages used to hit nginx 504 (org 18).
const PREVIEW_PAGE_SIZE = 25
type ImportReviewGroup = 'price' | 'new' | 'matching'

const IMPORT_PREVIEW_COLUMNS: TableColumnDef[] = [
  { id: 'row', label: '#', required: true, pin: 'start', defaultWidth: 56, minWidth: 40 },
  { id: 'name', label: 'Наименование', required: true, defaultWidth: 220, minWidth: 120 },
  { id: 'price', label: 'Цена', defaultWidth: 88, minWidth: 64 },
  { id: 'unit', label: 'Ед. изм.', defaultWidth: 80, minWidth: 56 },
  { id: 'brand', label: 'Бренд', defaultWidth: 110, minWidth: 64 },
  { id: 'country', label: 'Страна', defaultWidth: 100, minWidth: 64 },
  { id: 'type', label: 'Тип', defaultWidth: 140, minWidth: 80 },
  { id: 'status', label: 'Тип определения', defaultWidth: 130, minWidth: 80 },
  {
    id: 'actions',
    label: 'Действия',
    required: true,
    pin: 'end',
    hideInSettings: true,
    defaultWidth: 96,
    minWidth: 80,
  },
]

const applyPreviewRowsExpiry = (
  preview: CatalogImportPreviewDto,
  expiresAt: string | null,
): CatalogImportPreviewDto =>
  expiresAt ? { ...preview, expiresAt } : preview

const columnSamplePreview = (values: string[]): string => {
  const trimmed = values.filter((value) => value.trim()).slice(0, 3)
  return trimmed.length > 0 ? trimmed.join(', ') : 'нет примеров'
}

const columnMappingsEqual = (left: ColumnMapping, right: ColumnMapping): boolean =>
  CATALOG_IMPORT_FIELDS.every((field) => (left[field] ?? null) === (right[field] ?? null))

const applyDraft = (draft: CatalogImportDraft) => ({
  preview: draft.preview,
  columnMapping: draft.columnMapping,
  visibleRows: draft.visibleRows,
  previewPage: draft.previewPage ?? Math.floor((draft.offset ?? 0) / PREVIEW_PAGE_SIZE),
  rowTypeOverrides: draft.rowTypeOverrides,
  rowFieldOverrides: draft.rowFieldOverrides,
  excludedRowNumbers: new Set(draft.excludedRowNumbers),
  matchingChangesFirst: draft.matchingChangesFirst ?? false,
  matchingChangeRowNumbers: draft.matchingChangeRowNumbers ?? [],
  privateSupplierId: draft.privateSupplierId ?? null,
  offset: draft.offset ?? (draft.previewPage ?? 0) * PREVIEW_PAGE_SIZE,
})

const ReplaceFileButton = ({
  onReplaceFile,
  replaceDisabled = false,
}: {
  onReplaceFile: () => void
  replaceDisabled?: boolean
}) => (
  <button
    type="button"
    className="echat-btn echat-btn--ghost echat-btn--sm"
    disabled={replaceDisabled}
    onClick={onReplaceFile}
  >
    Заменить файл
  </button>
)

const ImportAnalysisPanel = ({
  preview,
  onReplaceFile,
  replaceDisabled = false,
  showReplaceButton = true,
}: {
  preview: CatalogImportPreviewDto
  onReplaceFile: () => void
  replaceDisabled?: boolean
  showReplaceButton?: boolean
}) => {
  const minutesLeft = previewSessionMinutesLeft(preview)

  return (
    <article className="echat-panel market-import-analysis">
      <h2 className="echat-panel__subtitle">{preview.filename}</h2>
      <p className="echat-muted">
        {preview.totalRows} строк · {preview.columns.length} столбцов
        {preview.savedProfileApplied ? ' · применён сохранённый профиль' : ''}
      </p>
      {preview.expiresAt ? (
        <p className="echat-muted">Черновик на сервере до {formatDraftExpiry(preview.expiresAt)}</p>
      ) : null}
      {minutesLeft != null && minutesLeft <= 60 ? (
        <p className="echat-error" role="status">
          {minutesLeft <= 0
            ? 'Сессия импорта истекла — загрузите файл заново.'
            : `Сессия импорта истечёт через ${minutesLeft} мин. Завершите импорт или загрузите прайс снова.`}
        </p>
      ) : null}
      {showReplaceButton ? (
        <div className="market-import-analysis__actions">
          <ReplaceFileButton onReplaceFile={onReplaceFile} replaceDisabled={replaceDisabled} />
        </div>
      ) : null}
    </article>
  )
}

/** Первая стадия хода загрузки — пока ядро читает файл и строит превью. */
const ANALYZING_MESSAGE = 'Начинаем анализ прайс-листа'

export const SupplierCatalogImportScreen = () => {
  const { session } = useAuth()
  const { activeVenueId } = useActiveVenue()
  const navigate = useNavigate()
  const location = useLocation()
  const { organizationId: organizationIdParam } = useParams()
  const routeOrganizationId = organizationIdParam ? Number(organizationIdParam) : null
  const hasRouteOrg =
    routeOrganizationId != null && Number.isFinite(routeOrganizationId) && routeOrganizationId > 0
  // «Мои прайсы»: и покупатель (/my-prices/import), и оператор (/operator/buyers/:id/prices/import).
  const isRestaurantMyPricesImport =
    location.pathname.includes('/my-prices/import') || location.pathname.endsWith('/prices/import')
  const importTarget: CatalogImportTarget = isRestaurantMyPricesImport ? 'restaurant-my-prices' : 'supplier'
  const columnPrefs = useTableColumnPrefs(
    isRestaurantMyPricesImport ? 'buyer:import-preview' : 'supplier:import-preview',
    IMPORT_PREVIEW_COLUMNS,
  )
  const organizationId =
    hasRouteOrg && routeOrganizationId != null ? routeOrganizationId : (session?.organizationId ?? 0)
  const catalogPath = isRestaurantMyPricesImport
    ? hasRouteOrg && routeOrganizationId != null
      ? operatorBuyerPricesPath(routeOrganizationId)
      : '/my-prices'
    : hasRouteOrg && routeOrganizationId != null
      ? operatorSupplierCatalogPath(routeOrganizationId)
      : '/catalog'
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const previewTableRef = useRef<HTMLDivElement | null>(null)
  const skipDraftOfferRef = useRef(Boolean((location.state as { preview?: CatalogImportPreviewDto } | null)?.preview))

  const initialPreview = (location.state as { preview?: CatalogImportPreviewDto } | null)?.preview
  const initialMapping = initialPreview ? columnMappingFromPreview(initialPreview) : {}

  const [preview, setPreview] = useState<CatalogImportPreviewDto | null>(initialPreview ?? null)
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>(() => initialMapping)
  /** Mapping last applied to the visible match-preview rows. */
  const [appliedColumnMapping, setAppliedColumnMapping] = useState<ColumnMapping>(() => initialMapping)
  const [visibleRows, setVisibleRows] = useState<ImportPreviewRowDto[]>(
    () => initialPreview?.sampleRows ?? [],
  )
  const [knownPreviewRows, setKnownPreviewRows] = useState<Record<number, ImportPreviewRowDto>>(
    () => Object.fromEntries((initialPreview?.sampleRows ?? []).map((row) => [row.rowNumber, row])),
  )
  const [previewPage, setPreviewPage] = useState(0)
  const [rowTypeOverrides, setRowTypeOverrides] = useState<Record<number, number>>({})
  const [rowFieldOverrides, setRowFieldOverrides] = useState<
    Record<number, CatalogImportRowFieldOverride>
  >({})
  const [excludedRowNumbers, setExcludedRowNumbers] = useState<Set<number>>(new Set())
  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const [productTypes, setProductTypes] = useState<ProductTypeDto[]>([])
  const [brandOptions, setBrandOptions] = useState<string[]>([])
  const [countryOptions, setCountryOptions] = useState<string[]>([])
  const [pendingDraft, setPendingDraft] = useState<CatalogImportDraft | null>(null)
  const [sessionLookupDone, setSessionLookupDone] = useState(
    () => Boolean(initialPreview) || skipDraftOfferRef.current,
  )
  const [savingDraft, setSavingDraft] = useState(false)
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null)
  const lastServerSaveKeyRef = useRef('')
  const [result, setResult] = useState<CatalogImportConfirmResultDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null)
  /** Ход подтверждения: «Разобрали N позиций из M». null — подтверждение не идёт. */
  const [importProgress, setImportProgress] = useState<ImportJobProgress | null>(null)
  const progressAbortRef = useRef<AbortController | null>(null)
  useEffect(() => () => progressAbortRef.current?.abort(), [])
  const [pageLoading, setPageLoading] = useState(false)
  const [previewStale, setPreviewStale] = useState(false)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [billingPaywall, setBillingPaywall] = useState<{ title: string; message: string; planName: string } | null>(
    null,
  )
  const [privateSuppliers, setPrivateSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [supplierMode, setSupplierMode] = useState<'existing' | 'new'>('new')
  // Из карточки поставщика приходят с ?supplier=<id>: обновление прайса начинается там,
  // а не с выбора поставщика заново — поставщик уже выбран тем, что карточку открыли.
  const preselectedSupplierId = (() => {
    const raw = new URLSearchParams(location.search).get('supplier')
    if (!raw) return null
    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  })()
  const [selectedPrivateSupplierId, setSelectedPrivateSupplierId] = useState<number | null>(
    preselectedSupplierId,
  )
  const [newSupplierLegalName, setNewSupplierLegalName] = useState('')
  const [newSupplierOgrn, setNewSupplierOgrn] = useState('')
  const [supplierConfirmAction, setSupplierConfirmAction] = useState<
    'pick-file' | 'confirm-import' | 'confirm-price-only' | null
  >(null)
  const [matchingChangeCount, setMatchingChangeCount] = useState(
    () => initialPreview?.matchingChangeCount ?? 0,
  )
  const [matchingChangeCountComplete, setMatchingChangeCountComplete] = useState(
    () => initialPreview?.matchingChangeCountComplete ?? false,
  )
  const [highlightMatchingChanges, setHighlightMatchingChanges] = useState(false)
  const [wizardStep, setWizardStep] = useState<'mapping' | 'review'>('mapping')
  const [matchingChangeRowNumbers, setMatchingChangeRowNumbers] = useState<number[]>([])
  const [importSummaryComplete, setImportSummaryComplete] = useState(false)
  const [priceChangeCount, setPriceChangeCount] = useState(0)
  const [priceChangeRowNumbers, setPriceChangeRowNumbers] = useState<number[]>([])
  const [newItemCount, setNewItemCount] = useState(0)
  const [newItemRowNumbers, setNewItemRowNumbers] = useState<number[]>([])
  const [activeReviewGroup, setActiveReviewGroup] = useState<ImportReviewGroup | null>(null)

  const typeNames = new Map(productTypes.map((type) => [type.id, type.name]))
  const activeReviewRowNumbers =
    activeReviewGroup === 'price'
      ? priceChangeRowNumbers
      : activeReviewGroup === 'new'
        ? newItemRowNumbers
        : activeReviewGroup === 'matching'
          ? matchingChangeRowNumbers
          : null
  const previewDisplayTotalRows = activeReviewRowNumbers?.length ?? preview?.totalRows ?? 0
  const activeReviewGroupLabel =
    activeReviewGroup === 'price'
      ? 'Позиции с новой ценой'
      : activeReviewGroup === 'new'
        ? 'Новые позиции'
        : activeReviewGroup === 'matching'
          ? 'Изменения данных и категорий'
          : null
  const importSummaryChangeCount = priceChangeCount + newItemCount + matchingChangeCount
  const previewTotalPages = Math.max(1, Math.ceil(previewDisplayTotalRows / PREVIEW_PAGE_SIZE))
  const busy = loading || pageLoading

  const mergeKnownPreviewRows = useCallback((rows: ImportPreviewRowDto[]) => {
    setKnownPreviewRows((current) => {
      const next = { ...current }
      for (const row of rows) {
        next[row.rowNumber] = row
      }
      return next
    })
  }, [])

  const applyMatchingChangeMeta = useCallback((page: CatalogImportPreviewRowsDto | CatalogImportPreviewDto) => {
    if ('importSummaryComplete' in page && page.importSummaryComplete) {
      setImportSummaryComplete(true)
      setPriceChangeCount(page.priceChangeCount ?? 0)
      setPriceChangeRowNumbers(page.priceChangeRowNumbers ?? [])
      setNewItemCount(page.newItemCount ?? 0)
      setNewItemRowNumbers(page.newItemRowNumbers ?? [])
    }
    if ('matchingChangeCountComplete' in page && page.matchingChangeCountComplete) {
      setMatchingChangeCount(page.matchingChangeCount)
      setMatchingChangeCountComplete(true)
      if ('matchingChangeRowNumbers' in page) {
        setMatchingChangeRowNumbers(page.matchingChangeRowNumbers)
      }
      return
    }
    if (!matchingChangeCountComplete) {
      setMatchingChangeCount(page.matchingChangeCount)
    }
  }, [matchingChangeCountComplete])

  const resetExpiredImportSession = useCallback(() => {
    if (organizationId > 0) {
      clearCatalogImportDraft(organizationId)
    }
    setPreview(null)
    setColumnMapping({})
    setAppliedColumnMapping({})
    setVisibleRows([])
    setKnownPreviewRows({})
    setPreviewPage(0)
    setRowTypeOverrides({})
    setRowFieldOverrides({})
    setExcludedRowNumbers(new Set())
    setPreviewStale(false)
    setPendingDraft(null)
    setSessionExpired(true)
    setMatchingChangeCount(0)
    setMatchingChangeCountComplete(false)
    setHighlightMatchingChanges(false)
    setMatchingChangeRowNumbers([])
    setImportSummaryComplete(false)
    setPriceChangeCount(0)
    setPriceChangeRowNumbers([])
    setNewItemCount(0)
    setNewItemRowNumbers([])
    setActiveReviewGroup(null)
  }, [organizationId])

  const handleImportApiError = useCallback(
    (importError: unknown, fallback: string) => {
      if (isImportPreviewSessionApiError(importError)) {
        resetExpiredImportSession()
        setError(IMPORT_SESSION_EXPIRED_MESSAGE)
        return
      }
      const billingDetails = parseBillingError(importError)
      if (billingDetails) {
        setError(null)
        setBillingPaywall({
          title:
            billingDetails.errorCode === 'my_prices_refresh_cooldown'
              ? 'Обновление прайса'
              : 'Лимит «Мои прайсы»',
          message: billingErrorMessage(importError),
          planName: billingDetails.requiredPlanDisplayName ?? 'Стандарт',
        })
        return
      }
      setError(formatApiErrorMessage(importError, fallback))
    },
    [resetExpiredImportSession],
  )

  const proposedForField = (field: string) =>
    preview?.proposedMapping.find((mapping) => mapping.field === field)


  useEffect(() => {
    void listProductTypes().then(setProductTypes).catch(() => undefined)
  }, [])

  useEffect(() => {
    if (!visibleRows.length) {
      return
    }
    const knownIds = new Set(productTypes.map((type) => type.id))
    const referencedIds = visibleRows.flatMap((row) => {
      const effectiveId = effectiveImportRowTypeId(row, rowTypeOverrides)
      const candidateIds = row.matchPreview.candidates.map((candidate) => candidate.productTypeId)
      return effectiveId != null ? [effectiveId, ...candidateIds] : candidateIds
    })
    const missingIds = [
      ...new Set(referencedIds.filter((id): id is number => id != null && !knownIds.has(id))),
    ]
    if (missingIds.length === 0) {
      return
    }
    void resolveProductTypes(missingIds)
      .then((resolved) => {
        setProductTypes((current) => mergeProductTypes(current, resolved))
      })
      .catch(() => undefined)
  }, [visibleRows, rowTypeOverrides, productTypes])

  useEffect(() => {
    if (organizationId <= 0) {
      return
    }
    void (isRestaurantMyPricesImport
      ? listRestaurantMyPricesFilterOptions(organizationId)
      : listSupplierCatalogFilterOptions(organizationId))
      .then((options) => {
        setBrandOptions(options.brands)
        setCountryOptions(options.countries)
      })
      .catch(() => undefined)
  }, [organizationId, isRestaurantMyPricesImport])

  useEffect(() => {
    if (!isRestaurantMyPricesImport || organizationId <= 0) {
      return
    }
    void listRestaurantPrivateSuppliers(organizationId, activeVenueId)
      .then((suppliers) => {
        setPrivateSuppliers(suppliers)
        if (suppliers.length > 0) {
          setSupplierMode('existing')
          // Пришли из карточки поставщика — выбираем его, а не первого в списке.
          // Иначе «Обновить прайс» молча подставлял чужого, и заметить это можно было
          // только по названию в выпадающем списке.
          const preselected = suppliers.find((entry) => entry.id === preselectedSupplierId)
          setSelectedPrivateSupplierId(preselected?.id ?? suppliers[0]?.id ?? null)
        } else {
          setSupplierMode('new')
        }
      })
      .catch(() => undefined)
  }, [activeVenueId, organizationId, isRestaurantMyPricesImport, preselectedSupplierId])

  useEffect(() => {
    if (skipDraftOfferRef.current || initialPreview || organizationId <= 0) {
      setSessionLookupDone(true)
      return
    }
    let cancelled = false
    const restore = async () => {
      try {
        const session = await getCatalogImportPreviewSession(organizationId, importTarget)
        if (!cancelled) {
          setPendingDraft(draftFromServerSession(session, PREVIEW_PAGE_SIZE))
        }
      } catch (lookupError) {
        if (cancelled) {
          return
        }
        if (!(lookupError instanceof ApiError && lookupError.status === 404)) {
          const localDraft = loadCatalogImportDraft(organizationId)
          if (localDraft) {
            setPendingDraft(localDraft)
          }
          return
        }
        const draft = loadCatalogImportDraft(organizationId)
        if (draft) {
          setPendingDraft(draft)
        }
      } finally {
        if (!cancelled) {
          setSessionLookupDone(true)
        }
      }
    }
    void restore()
    return () => {
      cancelled = true
    }
  }, [initialPreview, organizationId, importTarget])

  useEffect(() => {
    if (!preview || result || organizationId <= 0) {
      return
    }
    const timer = window.setTimeout(() => {
      saveCatalogImportDraft(organizationId, {
        preview,
        columnMapping,
        rowTypeOverrides,
        rowFieldOverrides,
        excludedRowNumbers: [...excludedRowNumbers],
        visibleRows,
        offset: previewPage * PREVIEW_PAGE_SIZE,
        previewPage,
        matchingChangesFirst: highlightMatchingChanges,
        matchingChangeRowNumbers,
        privateSupplierId: isRestaurantMyPricesImport ? selectedPrivateSupplierId : null,
      })
    }, 400)
    return () => window.clearTimeout(timer)
  }, [
    preview,
    columnMapping,
    rowTypeOverrides,
    rowFieldOverrides,
    excludedRowNumbers,
    visibleRows,
    result,
    organizationId,
    previewPage,
    highlightMatchingChanges,
    matchingChangeRowNumbers,
    isRestaurantMyPricesImport,
    selectedPrivateSupplierId,
  ])

  useEffect(() => {
    if (!preview || result || organizationId <= 0 || sessionExpired) {
      return
    }
    const payload = {
      organizationId,
      previewToken: preview.previewToken,
      columnMapping,
      rowProductTypeOverrides: rowTypeOverrides,
      rowFieldOverrides,
      excludedRowNumbers: [...excludedRowNumbers],
      offset: previewPage * PREVIEW_PAGE_SIZE,
      matchingChangesFirst: highlightMatchingChanges,
      matchingChangeRowNumbers,
      privateSupplierId: isRestaurantMyPricesImport ? selectedPrivateSupplierId : null,
    }
    const saveKey = JSON.stringify(payload)
    if (saveKey === lastServerSaveKeyRef.current) {
      return
    }
    const timer = window.setTimeout(() => {
      void saveCatalogImportPreview(payload, importTarget)
        .then((saved) => {
          lastServerSaveKeyRef.current = saveKey
          setDraftSavedAt(saved.savedAt)
          setPreview((current) =>
            current ? applyPreviewRowsExpiry(current, saved.expiresAt) : current,
          )
        })
        .catch(() => undefined)
    }, 2500)
    return () => window.clearTimeout(timer)
  }, [
    preview,
    columnMapping,
    rowTypeOverrides,
    rowFieldOverrides,
    excludedRowNumbers,
    result,
    organizationId,
    previewPage,
    highlightMatchingChanges,
    matchingChangeRowNumbers,
    isRestaurantMyPricesImport,
    selectedPrivateSupplierId,
    sessionExpired,
    importTarget,
  ])

  useEffect(() => {
    if (!preview?.expiresAt || result) {
      return
    }
    const checkExpiry = () => {
      if (isPreviewSessionExpired(preview)) {
        resetExpiredImportSession()
        setError(IMPORT_SESSION_EXPIRED_MESSAGE)
      }
    }
    checkExpiry()
    const timer = window.setInterval(checkExpiry, 30_000)
    return () => window.clearInterval(timer)
  }, [preview, result, resetExpiredImportSession])

  const handleResumeDraft = async () => {
    if (!pendingDraft || organizationId <= 0) {
      return
    }
    if (isPreviewSessionExpired(pendingDraft.preview)) {
      clearCatalogImportDraft(organizationId)
      setPendingDraft(null)
      setSessionExpired(true)
      setError(IMPORT_SESSION_EXPIRED_MESSAGE)
      return
    }

    setLoading(true)
    setLoadingMessage('Проверяем сессию импорта…')
    setError(null)
    try {
      const next = applyDraft(pendingDraft)
      const page = await importCatalogPreviewRows(
        {
          organizationId,
          previewToken: pendingDraft.preview.previewToken,
          columnMapping: pendingDraft.columnMapping,
          offset: next.offset,
          limit: PREVIEW_PAGE_SIZE,
          ...(next.matchingChangesFirst
            ? {
                matchingChangesFirst: true,
                ...(next.matchingChangeRowNumbers.length > 0
                  ? { matchingChangeRowNumbers: next.matchingChangeRowNumbers }
                  : {}),
              }
            : {}),
          ...(isRestaurantMyPricesImport && next.privateSupplierId != null
            ? { privateSupplierId: next.privateSupplierId }
            : {}),
        },
        importTarget,
      )
      const restoredPreview = page.expiresAt
        ? applyPreviewRowsExpiry(next.preview, page.expiresAt)
        : next.preview
      setWizardStep('review')
      setPreview({
        ...restoredPreview,
        totalRows: page.totalRows || restoredPreview.totalRows,
        sampleRows: page.rows,
      })
      setColumnMapping(next.columnMapping)
      setAppliedColumnMapping(next.columnMapping)
      setVisibleRows(page.rows.length > 0 ? page.rows : next.visibleRows)
      setKnownPreviewRows(
        Object.fromEntries((page.rows.length > 0 ? page.rows : next.visibleRows).map((row) => [row.rowNumber, row])),
      )
      setPreviewPage(Math.floor((page.offset ?? next.offset) / PREVIEW_PAGE_SIZE))
      setRowTypeOverrides(next.rowTypeOverrides)
      setRowFieldOverrides(next.rowFieldOverrides)
      setExcludedRowNumbers(next.excludedRowNumbers)
      setHighlightMatchingChanges(next.matchingChangesFirst)
      setMatchingChangeRowNumbers(
        page.matchingChangeRowNumbers.length > 0
          ? page.matchingChangeRowNumbers
          : next.matchingChangeRowNumbers,
      )
      if (next.privateSupplierId != null) {
        setSupplierMode('existing')
        setSelectedPrivateSupplierId(next.privateSupplierId)
      }
      applyMatchingChangeMeta(page)
      setPreviewStale(false)
      setPendingDraft(null)
      setResult(null)
      setSessionExpired(false)
      lastServerSaveKeyRef.current = ''
    } catch (resumeError) {
      handleImportApiError(resumeError, 'Не удалось восстановить черновик импорта')
      setPendingDraft(null)
    } finally {
      setLoading(false)
      setLoadingMessage(null)
    }
  }

  const handleDiscardDraft = () => {
    if (organizationId > 0) {
      clearCatalogImportDraft(organizationId)
      void discardCatalogImportPreviewSession(
        organizationId,
        pendingDraft?.preview.previewToken,
        importTarget,
      ).catch(() => undefined)
    }
    setPendingDraft(null)
  }

  const buildServerDraftPayload = () => {
    if (!preview) {
      return null
    }
    return {
      organizationId,
      previewToken: preview.previewToken,
      columnMapping,
      rowProductTypeOverrides: rowTypeOverrides,
      rowFieldOverrides,
      excludedRowNumbers: [...excludedRowNumbers],
      offset: previewPage * PREVIEW_PAGE_SIZE,
      matchingChangesFirst: highlightMatchingChanges,
      matchingChangeRowNumbers,
      privateSupplierId: isRestaurantMyPricesImport ? selectedPrivateSupplierId : null,
    }
  }

  const handleSaveDraft = async () => {
    const currentPreview = preview
    const payload = buildServerDraftPayload()
    if (!currentPreview || !payload || organizationId <= 0) {
      return
    }
    setSavingDraft(true)
    setError(null)
    try {
      const saved = await saveCatalogImportPreview(payload, importTarget)
      lastServerSaveKeyRef.current = JSON.stringify(payload)
      setDraftSavedAt(saved.savedAt)
      const nextPreview = applyPreviewRowsExpiry(currentPreview, saved.expiresAt)
      setPreview(nextPreview)
      saveCatalogImportDraft(organizationId, {
        preview: nextPreview,
        columnMapping,
        rowTypeOverrides,
        rowFieldOverrides,
        excludedRowNumbers: [...excludedRowNumbers],
        visibleRows,
        offset: payload.offset,
        previewPage,
        matchingChangesFirst: highlightMatchingChanges,
        matchingChangeRowNumbers,
        privateSupplierId: payload.privateSupplierId,
      })
    } catch (saveError) {
      handleImportApiError(saveError, 'Не удалось сохранить черновик импорта')
    } finally {
      setSavingDraft(false)
    }
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || organizationId <= 0) {
      return
    }

    setLoading(true)
    setLoadingMessage(ANALYZING_MESSAGE)
    setError(null)
    setResult(null)
    try {
      const nextPreview = await importCatalogPreview(organizationId, file, importTarget)
      const mapping = columnMappingFromPreview(nextPreview)
      clearCatalogImportDraft(organizationId)
      setWizardStep('mapping')
      setPreview(nextPreview)
      setColumnMapping(mapping)
      setAppliedColumnMapping(mapping)
      setVisibleRows(nextPreview.sampleRows)
      setKnownPreviewRows(
        Object.fromEntries(nextPreview.sampleRows.map((row) => [row.rowNumber, row])),
      )
      setPreviewPage(0)
      setRowTypeOverrides({})
      setRowFieldOverrides({})
      setExcludedRowNumbers(new Set())
      setExpandedRow(null)
      setPreviewStale(false)
      setPendingDraft(null)
      setSessionExpired(false)
      setMatchingChangeCount(nextPreview.matchingChangeCount)
      setMatchingChangeCountComplete(nextPreview.matchingChangeCountComplete)
      setHighlightMatchingChanges(false)
      setMatchingChangeRowNumbers([])
      setImportSummaryComplete(false)
      setPriceChangeCount(0)
      setPriceChangeRowNumbers([])
      setNewItemCount(0)
      setNewItemRowNumbers([])
      setActiveReviewGroup(null)
    } catch (uploadError) {
      setError(formatApiErrorMessage(uploadError, 'Не удалось загрузить файл'))
    } finally {
      setLoading(false)
      setLoadingMessage(null)
    }
  }

  const refreshPreviewRows = useCallback(async () => {
    if (!preview || organizationId <= 0) {
      return
    }
    setLoading(true)
    setLoadingMessage('Обновляем предпросмотр…')
    setError(null)
    try {
      const page = await importCatalogPreviewRows(
        {
          organizationId,
          previewToken: preview.previewToken,
          columnMapping,
          offset: 0,
          limit: PREVIEW_PAGE_SIZE,
          ...(isRestaurantMyPricesImport && selectedPrivateSupplierId != null
            ? { privateSupplierId: selectedPrivateSupplierId }
            : {}),
        },
        importTarget,
      )
      setPreview((current) =>
        current
          ? {
              ...applyPreviewRowsExpiry(current, page.expiresAt),
              totalRows: page.totalRows,
              sampleRows: page.rows,
            }
          : current,
      )
      setVisibleRows(page.rows)
      setKnownPreviewRows(Object.fromEntries(page.rows.map((row) => [row.rowNumber, row])))
      setPreviewPage(0)
      setAppliedColumnMapping({ ...columnMapping })
      setRowTypeOverrides({})
      setRowFieldOverrides({})
      setExcludedRowNumbers(new Set())
      setExpandedRow(null)
      setPreviewStale(false)
      setHighlightMatchingChanges(false)
      setMatchingChangeRowNumbers([])
      setImportSummaryComplete(false)
      setPriceChangeCount(0)
      setPriceChangeRowNumbers([])
      setNewItemCount(0)
      setNewItemRowNumbers([])
      setActiveReviewGroup(null)
      applyMatchingChangeMeta(page)
    } catch (refreshError) {
      handleImportApiError(refreshError, 'Не удалось обновить предпросмотр')
    } finally {
      setLoading(false)
      setLoadingMessage(null)
    }
  }, [
    columnMapping,
    preview,
    organizationId,
    handleImportApiError,
    importTarget,
    applyMatchingChangeMeta,
    isRestaurantMyPricesImport,
    selectedPrivateSupplierId,
  ])

  const handleMappingChange = (field: string, columnIndex: number | null) => {
    const next = { ...columnMapping, [field]: columnIndex }
    setColumnMapping(next)
    setPreviewStale(!columnMappingsEqual(next, appliedColumnMapping))
  }

  const handlePreviewPageChange = async (nextPage: number) => {
    if (!preview || organizationId <= 0 || pageLoading || loading || previewStale) {
      return
    }
    if (nextPage < 0 || nextPage >= previewTotalPages || nextPage === previewPage) {
      return
    }
    const previousPage = previewPage
    setPreviewPage(nextPage)
    setPageLoading(true)
    setError(null)
    try {
      const page = await importCatalogPreviewRows(
        {
          organizationId,
          previewToken: preview.previewToken,
          columnMapping,
          offset: nextPage * PREVIEW_PAGE_SIZE,
          limit: PREVIEW_PAGE_SIZE,
          ...(activeReviewRowNumbers != null
            ? { filterRowNumbers: activeReviewRowNumbers }
            : highlightMatchingChanges
            ? {
                matchingChangesFirst: true,
                ...(matchingChangeRowNumbers.length > 0
                  ? { matchingChangeRowNumbers }
                  : {}),
              }
            : {}),
          ...(isRestaurantMyPricesImport && selectedPrivateSupplierId != null
            ? { privateSupplierId: selectedPrivateSupplierId }
            : {}),
        },
        importTarget,
      )
      setPreview((current) =>
        current
          ? {
              ...applyPreviewRowsExpiry(current, page.expiresAt),
              totalRows: page.totalRows,
            }
          : current,
      )
      setVisibleRows(page.rows)
      mergeKnownPreviewRows(page.rows)
      applyMatchingChangeMeta(page)
      setExpandedRow(null)
    } catch (pageError) {
      setPreviewPage(previousPage)
      handleImportApiError(pageError, 'Не удалось загрузить страницу предпросмотра')
    } finally {
      setPageLoading(false)
    }
  }

  const openReview = async (
    scanMatchingChanges: boolean,
    group: ImportReviewGroup | null = null,
  ) => {
    if (!preview || organizationId <= 0) {
      return
    }
    const groupRowNumbers =
      group === 'price'
        ? priceChangeRowNumbers
        : group === 'new'
          ? newItemRowNumbers
          : group === 'matching'
            ? matchingChangeRowNumbers
            : null
    const groupLabel =
      group === 'price'
        ? 'изменённые цены'
        : group === 'new'
          ? 'новые позиции'
          : group === 'matching'
            ? 'изменения данных и категорий'
            : null
    setLoading(true)
    setLoadingMessage(
      scanMatchingChanges
        ? 'Сверяем позиции с каталогом…'
        : groupLabel
          ? `Показываем: ${groupLabel}…`
          : 'Показываем все позиции…',
    )
    setError(null)
    try {
      const page = await importCatalogPreviewRows(
        {
          organizationId,
          previewToken: preview.previewToken,
          columnMapping,
          offset: 0,
          limit: PREVIEW_PAGE_SIZE,
          ...(scanMatchingChanges && group == null ? { matchingChangesFirst: true } : {}),
          ...(scanMatchingChanges ? { scanMatchingChanges: true } : {}),
          ...(scanMatchingChanges && matchingChangeRowNumbers.length > 0
            ? { matchingChangeRowNumbers }
            : {}),
          ...(groupRowNumbers != null ? { filterRowNumbers: groupRowNumbers } : {}),
          ...(isRestaurantMyPricesImport && selectedPrivateSupplierId != null
            ? { privateSupplierId: selectedPrivateSupplierId }
            : {}),
        },
        importTarget,
      )
      setPreview((current) =>
        current
          ? {
              ...applyPreviewRowsExpiry(current, page.expiresAt),
              totalRows: page.totalRows,
              sampleRows: page.rows,
            }
          : current,
      )
      setVisibleRows(page.rows)
      mergeKnownPreviewRows(page.rows)
      applyMatchingChangeMeta(page)
      setPreviewPage(0)
      setExpandedRow(null)
      setActiveReviewGroup(group)
      const summaryCount =
        (page.priceChangeCount ?? 0) +
        (page.newItemCount ?? 0) +
        page.matchingChangeCount
      setHighlightMatchingChanges(summaryCount > 0)
      setWizardStep('review')
      if (typeof previewTableRef.current?.scrollIntoView === 'function') {
        previewTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (reviewError) {
      handleImportApiError(reviewError, 'Не удалось показать позиции с правками')
    } finally {
      setLoading(false)
      setLoadingMessage(null)
    }
  }

  const handleStartReview = async () => openReview(true)

  const handleRowTypeOverride = (rowNumber: number, productTypeId: number | null) => {
    setRowTypeOverrides((current) => {
      const next = { ...current }
      if (productTypeId == null) {
        delete next[rowNumber]
      } else {
        next[rowNumber] = productTypeId
      }
      return next
    })
  }

  const handleRowFieldOverride = (
    rowNumber: number,
    override: CatalogImportRowFieldOverride | null,
  ) => {
    setRowFieldOverrides((current) => {
      const next = { ...current }
      if (override == null) {
        delete next[rowNumber]
      } else {
        next[rowNumber] = override
      }
      return next
    })
  }

  const handleToggleExcludedRow = (rowNumber: number) => {
    setExcludedRowNumbers((current) => {
      const next = new Set(current)
      if (next.has(rowNumber)) {
        next.delete(rowNumber)
      } else {
        next.add(rowNumber)
      }
      return next
    })
  }

  const resolveImportSupplierLabel = (): string => {
    if (supplierMode === 'new') {
      return newSupplierLegalName.trim() || 'новый поставщик'
    }
    const selected = privateSuppliers.find((supplier) => supplier.id === selectedPrivateSupplierId)
    if (!selected) {
      return 'выбранный поставщик'
    }
    return selected.legalName || selected.ogrn || selected.inn || ''
  }

  const validateMyPricesSupplierSelection = (): boolean => {
    if (!isRestaurantMyPricesImport) {
      return true
    }
    if (supplierMode === 'existing' && selectedPrivateSupplierId == null) {
      setError('Выберите профиль поставщика для импорта.')
      return false
    }
    if (supplierMode === 'new') {
      if (!newSupplierLegalName.trim()) {
        setError('Укажите юридическое наименование поставщика.')
        return false
      }
      const ogrnError = ogrnValidationMessage(newSupplierOgrn, false)
      if (ogrnError) {
        setError(ogrnError)
        return false
      }
    }
    return true
  }

  const openSupplierConfirm = (action: 'pick-file' | 'confirm-import' | 'confirm-price-only') => {
    setError(null)
    if (!validateMyPricesSupplierSelection()) {
      return
    }
    setSupplierConfirmAction(action)
  }

  const handleConfirm = async (
    confirmedPrivateSupplierId?: number | null,
    updateMode?: ExistingItemUpdateMode | null,
  ) => {
    if (!preview || organizationId <= 0 || loading || pageLoading) {
      return
    }
    if (isPreviewSessionExpired(preview)) {
      resetExpiredImportSession()
      setError(IMPORT_SESSION_EXPIRED_MESSAGE)
      return
    }
    if (columnMapping.name == null) {
      setError('Укажите столбец для поля «Наименование».')
      return
    }
    if (isRestaurantMyPricesImport) {
      if (supplierMode === 'existing' && selectedPrivateSupplierId == null && confirmedPrivateSupplierId == null) {
        setError('Выберите профиль поставщика для импорта.')
        return
      }
      if (supplierMode === 'new' && confirmedPrivateSupplierId == null) {
        if (!newSupplierLegalName.trim()) {
          setError('Укажите юридическое наименование поставщика.')
          return
        }
        const ogrnError = ogrnValidationMessage(newSupplierOgrn, false)
        if (ogrnError) {
          setError(ogrnError)
          return
        }
      }
    }

    const effectivePrivateSupplierId =
      confirmedPrivateSupplierId != null ? confirmedPrivateSupplierId : selectedPrivateSupplierId
    const useExistingSupplier =
      isRestaurantMyPricesImport &&
      (confirmedPrivateSupplierId != null || supplierMode === 'existing')
    const mode = updateMode ?? null

    if (!mode && (!matchingChangeCountComplete || !importSummaryComplete)) {
      setLoading(true)
      setLoadingMessage('Сверяем позиции с каталогом…')
      setError(null)
      try {
        const page = await importCatalogPreviewRows(
          {
            organizationId,
            previewToken: preview.previewToken,
            columnMapping,
            offset: 0,
            limit: PREVIEW_PAGE_SIZE,
            scanMatchingChanges: true,
            ...(useExistingSupplier && effectivePrivateSupplierId != null
              ? { privateSupplierId: effectivePrivateSupplierId }
              : {}),
          },
          importTarget,
        )
        applyMatchingChangeMeta(page)
        mergeKnownPreviewRows(page.rows)
        if (previewPage === 0) {
          setVisibleRows(page.rows)
        }
        setPreview((current) =>
          current ? { ...applyPreviewRowsExpiry(current, page.expiresAt), totalRows: page.totalRows } : current,
        )
      } catch (scanError) {
        handleImportApiError(scanError, 'Не удалось сверить позиции с каталогом')
        return
      } finally {
        setLoading(false)
        setLoadingMessage(null)
      }
    }

    setLoading(true)
    setLoadingMessage('Импортируем позиции…')
    setError(null)
    // Фоновая задача и достоверный счётчик строк есть только у «Моих прайсов».
    // Для обычного импорта поставщика оставляем нейтральный индикатор, иначе «0 из N»
    // выглядело бы как настоящий, но не существующий ход разбора.
    const progressAbort = isRestaurantMyPricesImport ? new AbortController() : null
    if (progressAbort) {
      progressAbortRef.current?.abort()
      progressAbortRef.current = progressAbort
      setImportProgress({ processedRows: 0, totalRows: preview.totalRows })
    } else {
      setImportProgress(null)
    }
    try {
      const confirmResult = await confirmCatalogImportWithProgress(
        {
          organizationId,
          venueId: isRestaurantMyPricesImport ? activeVenueId : null,
          previewToken: preview.previewToken,
          columnMapping,
          rowProductTypeOverrides: buildConfirmProductTypeOverrides(
            Object.values(knownPreviewRows),
            rowTypeOverrides,
            excludedRowNumbers,
          ),
          rowMatcherSuggestions: buildConfirmMatcherSuggestions(
            Object.values(knownPreviewRows),
            rowTypeOverrides,
            excludedRowNumbers,
          ),
          rowFieldOverrides,
          excludedRowNumbers: [...excludedRowNumbers],
          ...(useExistingSupplier && effectivePrivateSupplierId != null
            ? { privateSupplierId: effectivePrivateSupplierId }
            : {}),
          ...(isRestaurantMyPricesImport && !useExistingSupplier && supplierMode === 'new'
            ? {
                newSupplier: {
                  legalName: newSupplierLegalName.trim(),
                  ogrn: newSupplierOgrn.trim(),
                },
              }
            : {}),
          ...(mode ? { existingItemUpdateMode: mode } : {}),
        },
        importTarget,
        (progress) => {
          if (isRestaurantMyPricesImport) {
            setImportProgress({
              processedRows: progress.processedRows,
              totalRows: progress.totalRows || preview.totalRows,
            })
          }
        },
        progressAbort?.signal,
      )
      clearCatalogImportDraft(organizationId)
      setResult(confirmResult)
      setPreview(null)
      setVisibleRows([])
      setKnownPreviewRows({})
      setPreviewPage(0)
      setRowTypeOverrides({})
      setRowFieldOverrides({})
      setExcludedRowNumbers(new Set())
      setMatchingChangeCount(0)
      setMatchingChangeCountComplete(false)
      setMatchingChangeRowNumbers([])
      setImportSummaryComplete(false)
      setPriceChangeCount(0)
      setPriceChangeRowNumbers([])
      setNewItemCount(0)
      setNewItemRowNumbers([])
      setActiveReviewGroup(null)
      setHighlightMatchingChanges(false)
    } catch (confirmError) {
      if (!(confirmError instanceof DOMException && confirmError.name === 'AbortError')) {
        handleImportApiError(confirmError, 'Не удалось импортировать')
      }
    } finally {
      setLoading(false)
      setLoadingMessage(null)
      setImportProgress(null)
    }
  }

  const handleSupplierConfirmAccepted = (privateSupplierId: number | null) => {
    const action = supplierConfirmAction
    setSupplierConfirmAction(null)
    if (privateSupplierId != null) {
      setSupplierMode('existing')
      setSelectedPrivateSupplierId(privateSupplierId)
    }
    if (action === 'pick-file') {
      fileInputRef.current?.click()
      return
    }
    if (action === 'confirm-import' || action === 'confirm-price-only') {
      void handleConfirm(privateSupplierId, action === 'confirm-price-only' ? 'price_only' : undefined)
    }
  }

  if (organizationId <= 0) {
    return (
      <section className="market-page market-page--wide">
        <p className="echat-error">Не удалось определить организацию для импорта.</p>
      </section>
    )
  }

  const missingRequiredFields = (preview?.proposedMapping ?? [])
    .filter((mapping) => mapping.required && columnMapping[mapping.field as keyof ColumnMapping] == null)
    .map((mapping) => catalogImportFieldLabel(mapping.field as CatalogImportField))
  const stepLabels = isRestaurantMyPricesImport
    ? ['Поставщик', 'Файл и столбцы', 'Проверка строк', 'Импорт']
    : ['Файл и столбцы', 'Проверка строк', 'Импорт']
  const stepOffset = isRestaurantMyPricesImport ? 1 : 0
  const currentStep = result
    ? stepLabels.length - 1
    : preview
      ? stepOffset + (wizardStep === 'mapping' ? 0 : 1)
      : isRestaurantMyPricesImport && privateSuppliers.length > 0 && !pendingDraft
        ? 0
        : stepOffset

  return (
    <section className="market-page market-page--wide">
      <MarketPageHeader
        title={isRestaurantMyPricesImport ? 'Импорт в «Мои прайсы»' : 'Импорт прайса'}
        description="Файлы xlsx, xls или csv · сессия предпросмотра на сервере 72 ч"
        actions={
          <button type="button" className="echat-back" onClick={() => navigate(catalogPath)}>
            {isRestaurantMyPricesImport ? '← К моим прайсам' : '← К каталогу'}
          </button>
        }
      />

      <div id="import-stepper">
        <ImportStepper steps={stepLabels} current={currentStep} />
      </div>

      <PaywallModal
        open={billingPaywall != null}
        title={billingPaywall?.title ?? ''}
        message={billingPaywall?.message ?? ''}
        onDismiss={() => setBillingPaywall(null)}
      />

      <input
        ref={fileInputRef}
        type="file"
        className="visually-hidden"
        accept=".xlsx,.xls,.csv"
        onChange={(event) => void handleFileChange(event)}
      />

      {pendingDraft ? (
        <article className="echat-panel echat-panel--highlight">
          <h2 className="echat-panel__subtitle">Продолжить импорт?</h2>
          <p className="echat-panel__text">
            Найден черновик «{pendingDraft.preview.filename}»
            {(pendingDraft.previewPage ?? 0) > 0
              ? ` · вернём на страницу ${(pendingDraft.previewPage ?? 0) + 1}`
              : ''}
            . Сессия на сервере до {formatDraftExpiry(pendingDraft.preview.expiresAt)}.
          </p>
          <div className="market-inline-actions">
            <button type="button" className="echat-btn echat-btn--primary" onClick={() => void handleResumeDraft()}>
              Продолжить
            </button>
            <button type="button" className="echat-btn echat-btn--ghost" onClick={handleDiscardDraft}>
              Начать заново
            </button>
          </div>
        </article>
      ) : null}

      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}
      {importProgress ? (
        <ImportProgress
          stage="parsing"
          processedRows={importProgress.processedRows}
          totalRows={importProgress.totalRows}
        />
      ) : loading && loadingMessage && (preview || pendingDraft || result) ? (
        <MarketLoadingIndicator message={loadingMessage} />
      ) : null}

      {result ? (
        <article className="echat-panel">
          {isRestaurantMyPricesImport ? (
            <>
              <ImportProgress stage="done" />
              {/* Статусы матчера пользователю «Моих прайсов» не показываем — только то, что стоит проверить. */}
              <p className="echat-panel__text">
                Загружено {result.imported} {pluralRu(result.imported, 'позиция', 'позиции', 'позиций')}
                {result.suggested + result.unmatched > 0
                  ? ` · ${result.suggested + result.unmatched} ${pluralRu(
                      result.suggested + result.unmatched,
                      'требует',
                      'требуют',
                      'требуют',
                    )} внимания`
                  : ' · все категории определены'}
              </p>
            </>
          ) : (
            <>
              <h2 className="echat-panel__subtitle">Импорт завершён</h2>
              <p className="echat-panel__text">Импортировано: {result.imported}</p>
              <p className="echat-muted">
                Определены автоматически: {result.autoMatched} · Требуют внимания: {result.suggested}{' '}
                · Без типа:{' '}
                {result.unmatched} · Обновлено: {result.updatedExisting}
              </p>
            </>
          )}
          <div className="market-inline-actions">
            <button type="button" className="echat-btn echat-btn--primary" onClick={() => navigate(catalogPath)}>
              Открыть каталог
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              onClick={() => {
                setResult(null)
                fileInputRef.current?.click()
              }}
            >
              Импортировать другой файл
            </button>
          </div>
        </article>
      ) : null}

      {!result && !preview && !pendingDraft ? (
        !sessionLookupDone || loading ? (
          loadingMessage === ANALYZING_MESSAGE ? (
            <ImportProgress stage="analyzing" />
          ) : (
            <article className="echat-panel">
              <MarketLoadingIndicator
                message={
                  loadingMessage
                    ?? (!sessionLookupDone ? 'Ищем сохранённый импорт…' : 'Распознаём прайс…')
                }
              />
              <div className="market-import-preview-skeleton" aria-hidden="true">
                <div className="market-import-preview-skeleton__row" />
                <div className="market-import-preview-skeleton__row" />
                <div className="market-import-preview-skeleton__row" />
                <div className="market-import-preview-skeleton__row" />
              </div>
            </article>
          )
        ) : isRestaurantMyPricesImport && privateSuppliers.length > 0 ? (
          <article className="echat-panel market-import-supplier">
            <header className="market-import-supplier__header">
              <h2 className="echat-panel__subtitle">Выберите поставщика</h2>
              <p className="market-muted market-import-supplier__lead">
                Укажите профиль поставщика, затем загрузите файл прайса.
              </p>
            </header>

            <div className="market-import-supplier__modes" role="radiogroup" aria-label="Режим поставщика">
              <label className="market-import-supplier__mode">
                <input
                  type="radio"
                  name="supplierModePick"
                  checked={supplierMode === 'existing'}
                  onChange={() => setSupplierMode('existing')}
                />
                <span>Существующий</span>
              </label>
              <label className="market-import-supplier__mode">
                <input
                  type="radio"
                  name="supplierModePick"
                  checked={supplierMode === 'new'}
                  onChange={() => setSupplierMode('new')}
                />
                <span>Новый</span>
              </label>
            </div>

            <div className="market-import-supplier__fields">
              {supplierMode === 'existing' ? (
                <SelectCombobox
                  className="market-import-supplier__profile"
                  label="Профиль"
                  value={selectedPrivateSupplierId != null ? String(selectedPrivateSupplierId) : ''}
                  options={privateSuppliers.map((supplier) => ({
                    value: String(supplier.id),
                    label: `${supplier.legalName}${supplier.ogrn ? ` (${ogrnLabel(supplier.ogrn)} ${supplier.ogrn})` : supplier.inn ? ` (ИНН ${supplier.inn})` : ''}`,
                  }))}
                  onChange={(next) => setSelectedPrivateSupplierId(next ? Number(next) : null)}
                />
              ) : (
                <>
                  <label className="echat-field market-import-supplier__field">
                    <span className="echat-field__label">Юридическое наименование *</span>
                    <input
                      className="echat-field__input"
                      value={newSupplierLegalName}
                      onChange={(event) => setNewSupplierLegalName(event.target.value)}
                    />
                  </label>
                  <label className="echat-field market-import-supplier__field">
                    <span className="echat-field__label">ОГРН или ОГРНИП</span>
                    <input
                      className="echat-field__input"
                      value={newSupplierOgrn}
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={15}
                          placeholder="необязательно"
                      onChange={(event) => setNewSupplierOgrn(event.target.value)}
                    />
                  </label>
                </>
              )}
            </div>

            <div className="market-inline-actions">
              {supplierMode === 'existing' ? (
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  onClick={() => setSupplierMode('new')}
                >
                  Новый поставщик
                </button>
              ) : null}
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                disabled={supplierMode === 'existing' && selectedPrivateSupplierId == null}
                onClick={() => openSupplierConfirm('pick-file')}
              >
                Выбрать файл
              </button>
            </div>
          </article>
        ) : (
          <MarketEmptyState
            title={sessionExpired ? 'Сессия импорта истекла' : 'Выберите файл прайса'}
            description={
              sessionExpired
                ? 'Пока вы правили категории в каталоге, серверная сессия предпросмотра закончилась. Загрузите тот же прайс снова — правки в каталоге сохранены.'
                : 'Система распознает столбцы и предложит сопоставление с категориями Провизиум. Перед импортом проверьте маппинг и строки с неуверенным матчингом.'
            }
            actions={[
              {
                label: 'Выбрать файл',
                onClick: () => fileInputRef.current?.click(),
              },
            ]}
          />
        )
      ) : null}

      {preview ? (
        <>
          {wizardStep === 'mapping' ? (
            <>
          {preview.warnings.length > 0 ? (
            <article className="echat-panel echat-panel--highlight">
              <h2 className="echat-panel__subtitle">Обратите внимание</h2>
              <ul>
                {preview.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </article>
          ) : null}

          {isRestaurantMyPricesImport ? (
            <div className="market-import-top-row">
              <article className="echat-panel market-import-supplier">
                <header className="market-import-supplier__header">
                  <h2 className="echat-panel__subtitle">Поставщик прайса</h2>
                  <p className="market-muted market-import-supplier__lead">
                    Укажите данные при первом импорте или выберите существующий профиль.
                  </p>
                </header>

                {privateSuppliers.length > 0 ? (
                  <div className="market-import-supplier__modes" role="radiogroup" aria-label="Режим поставщика">
                    <label className="market-import-supplier__mode">
                      <input
                        type="radio"
                        name="supplierMode"
                        checked={supplierMode === 'existing'}
                        onChange={() => setSupplierMode('existing')}
                      />
                      <span>Существующий</span>
                    </label>
                    <label className="market-import-supplier__mode">
                      <input
                        type="radio"
                        name="supplierMode"
                        checked={supplierMode === 'new'}
                        onChange={() => setSupplierMode('new')}
                      />
                      <span>Новый</span>
                    </label>
                  </div>
                ) : null}

                <div className="market-import-supplier__fields">
                  {supplierMode === 'existing' && privateSuppliers.length > 0 ? (
                    <div className="market-import-supplier__profile-row">
                      <SelectCombobox
                        className="market-import-supplier__profile"
                        label="Профиль"
                        value={selectedPrivateSupplierId != null ? String(selectedPrivateSupplierId) : ''}
                        options={privateSuppliers.map((supplier) => ({
                          value: String(supplier.id),
                          label: `${supplier.legalName}${supplier.ogrn ? ` (${ogrnLabel(supplier.ogrn)} ${supplier.ogrn})` : supplier.inn ? ` (ИНН ${supplier.inn})` : ''}`,
                        }))}
                        onChange={(next) =>
                          setSelectedPrivateSupplierId(next ? Number(next) : null)
                        }
                      />
                      <div className="market-import-supplier__replace">
                        <ReplaceFileButton
                          onReplaceFile={() => fileInputRef.current?.click()}
                          replaceDisabled={busy}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="echat-field market-import-supplier__field">
                        <span className="echat-field__label">Юридическое наименование *</span>
                        <input
                          className="echat-field__input"
                          value={newSupplierLegalName}
                          onChange={(event) => setNewSupplierLegalName(event.target.value)}
                        />
                      </label>
                      <label className="echat-field market-import-supplier__field">
                        <span className="echat-field__label">ОГРН или ОГРНИП</span>
                        <input
                          className="echat-field__input"
                          value={newSupplierOgrn}
                          inputMode="numeric"
                          autoComplete="off"
                          maxLength={15}
                          placeholder="необязательно"
                          onChange={(event) => setNewSupplierOgrn(event.target.value)}
                        />
                      </label>
                    </>
                  )}
                </div>
              </article>

              <ImportAnalysisPanel
                preview={preview}
                onReplaceFile={() => fileInputRef.current?.click()}
                replaceDisabled={busy}
                showReplaceButton={!(supplierMode === 'existing' && privateSuppliers.length > 0)}
              />
            </div>
          ) : (
            <ImportAnalysisPanel
              preview={preview}
              onReplaceFile={() => fileInputRef.current?.click()}
              replaceDisabled={busy}
            />
          )}

          <article
            id="import-column-mapping"
            className="echat-panel market-import-mapping-panel"
          >
            <div className="market-import-section-header">
              <h2 className="echat-panel__subtitle">Сопоставление столбцов</h2>
              {previewStale ? (
                <button
                  type="button"
                  className="echat-btn echat-btn--primary echat-btn--sm"
                  disabled={busy}
                  onClick={() => void refreshPreviewRows()}
                >
                  Обновить предпросмотр
                </button>
              ) : null}
            </div>
            <p className="echat-muted market-import-section-lead">
              Если автоопределение ошиблось (цена в «Стране», описание в «Бренде»), поправьте поля
              ниже и обновите предпросмотр.
            </p>
            <div className="market-import-mapping">
              {(() => {
                const renderField = (field: (typeof CATALOG_IMPORT_FIELDS)[number]) => {
                const proposed = proposedForField(field)
                    return (
                      <div key={field} className="echat-field market-import-mapping__row">
                        <SelectCombobox
                          label={catalogImportFieldLabel(field)}
                          value={columnMapping[field] != null ? String(columnMapping[field]) : ''}
                          options={[
                            { value: '', label: '— не использовать —' },
                            ...preview.columns.map((column) => ({
                              value: String(column.index),
                              label: `${column.header || `Столбец ${column.index + 1}`} (${columnSamplePreview(column.sampleValues)})`,
                            })),
                          ]}
                          onChange={(next) =>
                            handleMappingChange(field, next ? Number(next) : null)
                          }
                        />
                        {proposed?.source ? (
                          <span className="echat-muted market-import-mapping__hint">
                            {mappingSourceLabel(proposed.source)}
                          </span>
                        ) : null}
                      </div>
                    )
                }
                const isCollapsible = (field: (typeof CATALOG_IMPORT_FIELDS)[number]) => {
                  const proposed = proposedForField(field)
                  return (
                    !proposed?.required
                    && proposed?.columnIndex != null
                    && columnMapping[field] === proposed.columnIndex
                  )
                }
                const openFields = CATALOG_IMPORT_FIELDS.filter((field) => !isCollapsible(field))
                const foundFields = CATALOG_IMPORT_FIELDS.filter(isCollapsible)
                return (
                  <>
                    {openFields.map(renderField)}
                    {foundFields.length > 0 ? (
                      <details className="market-import-mapping__found">
                        <summary>
                          Определены автоматически: {foundFields.length} (
                          {foundFields.map((field) => catalogImportFieldLabel(field)).join(', ')})
                        </summary>
                        {foundFields.map(renderField)}
                      </details>
                    ) : null}
                  </>
                )
              })()}
            </div>

            {previewStale ? (
              <div className="market-inline-actions market-import-stale-actions">
                <p className="echat-muted">Маппинг изменился — обновите предпросмотр матчинга.</p>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={busy}
                  onClick={() => void refreshPreviewRows()}
                >
                  Обновить предпросмотр
                </button>
              </div>
            ) : null}
          </article>

          {missingRequiredFields.length > 0 ? (
            <p className="echat-error" role="status">
              Укажите обязательные столбцы: {missingRequiredFields.join(', ')}.
            </p>
          ) : null}
          <div className="market-inline-actions market-import-actions--sticky">
            <button
              type="button"
              className="echat-btn echat-btn--primary"
              disabled={busy || previewStale || missingRequiredFields.length > 0 || isPreviewSessionExpired(preview)}
              onClick={() => void handleStartReview()}
            >
              Далее: проверка строк
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={busy || savingDraft || previewStale || isPreviewSessionExpired(preview)}
              onClick={() => void handleSaveDraft()}
            >
              {savingDraft ? 'Сохраняем…' : 'Сохранить черновик'}
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              Другой файл
            </button>
            {draftSavedAt ? (
              <span className="echat-muted">Сохранено {formatDraftExpiry(draftSavedAt)}</span>
            ) : null}
          </div>
            </>
          ) : (
            <>
          <article className="echat-panel">
            <div className="market-import-section-header">
              <h2 className="echat-panel__subtitle">Предпросмотр матчинга</h2>
              <div className="market-import-section-header__actions">
                <TableColumnSettings prefs={columnPrefs} />
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost echat-btn--sm"
                  onClick={() => setWizardStep('mapping')}
                >
                  Изменить столбцы
                </button>
              </div>
            </div>
            <p className="market-import-summary" role="status">
              <span>Файл: <strong>{preview.filename}</strong></span>
              <span>Всего строк: <strong>{preview.totalRows}</strong></span>
              <span>Исключено: <strong>{excludedRowNumbers.size}</strong></span>
              {!importSummaryComplete ? (
                <span>
                  С правками матчинга:{' '}
                  <strong>{matchingChangeCount}{matchingChangeCountComplete ? '' : '+'}</strong>
                </span>
              ) : null}
            </p>
            {importSummaryComplete && importSummaryChangeCount > 0 ? (
              <aside className="market-import-change-summary" aria-labelledby="import-change-summary-title">
                <div className="market-import-change-summary__header">
                  <h3 id="import-change-summary-title">Изменения после импорта</h3>
                  <p>Ручные категории не пересчитываются и меняются только поштучно.</p>
                </div>
                <div className="market-import-change-summary__links">
                  {priceChangeCount > 0 ? (
                    <button
                      type="button"
                      className="market-import-change-summary__link"
                      disabled={busy}
                      aria-pressed={activeReviewGroup === 'price'}
                      onClick={() => void openReview(false, 'price')}
                    >
                      У {priceChangeCount}{' '}
                      {pluralRu(priceChangeCount, 'позиции', 'позиций', 'позиций')} изменилась цена
                    </button>
                  ) : null}
                  {newItemCount > 0 ? (
                    <button
                      type="button"
                      className="market-import-change-summary__link"
                      disabled={busy}
                      aria-pressed={activeReviewGroup === 'new'}
                      onClick={() => void openReview(false, 'new')}
                    >
                      Добавлено {newItemCount} новых позиций
                    </button>
                  ) : null}
                  {matchingChangeCount > 0 ? (
                    <button
                      type="button"
                      className="market-import-change-summary__link"
                      disabled={busy}
                      aria-pressed={activeReviewGroup === 'matching'}
                      onClick={() => void openReview(false, 'matching')}
                    >
                      Нужно проверить данные или категорию у {matchingChangeCount}{' '}
                      {pluralRu(matchingChangeCount, 'позиции', 'позиций', 'позиций')}
                    </button>
                  ) : null}
                </div>
              </aside>
            ) : null}
            {activeReviewGroupLabel ? (
              <div className="market-import-group-filter" role="status">
                <span>
                  Показана группа: <strong>{activeReviewGroupLabel}</strong> · {previewDisplayTotalRows}
                </span>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost echat-btn--sm"
                  disabled={busy}
                  onClick={() => void openReview(false)}
                >
                  Показать все позиции
                </button>
              </div>
            ) : null}
            <p className="echat-muted">
              Страница {previewPage + 1} из {previewTotalPages} · до {PREVIEW_PAGE_SIZE} строк на
              странице · всего {previewDisplayTotalRows}. Иконка карандаша правит поля одной позиции или
              категорию. Чтобы переназначить столбцы файла — «Изменить столбцы». Исключённые строки
              не попадут в импорт.
              {excludedRowNumbers.size > 0 ? ` Исключено: ${excludedRowNumbers.size}.` : ''}
            </p>
            {previewStale ? (
              <div className="market-import-stale-hint" role="status">
                <p className="echat-muted">
                  Сопоставление столбцов изменено — предпросмотр устарел. Обновите его перед импортом.
                </p>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary echat-btn--sm"
                  disabled={busy}
                  onClick={() => void refreshPreviewRows()}
                >
                  Обновить предпросмотр
                </button>
              </div>
            ) : null}
            {loading && loadingMessage === 'Обновляем предпросмотр…' && visibleRows.length === 0 ? (
              <div className="market-import-preview-skeleton" aria-hidden="true">
                <div className="market-import-preview-skeleton__row" />
                <div className="market-import-preview-skeleton__row" />
                <div className="market-import-preview-skeleton__row" />
              </div>
            ) : null}
            {visibleRows.length > 0 ? (
              <div
                ref={previewTableRef}
                className={`market-table-wrap${busy ? ' market-table-wrap--loading' : ''}`}
                aria-busy={busy}
              >
                {busy ? (
                  <div className="market-table-wrap__overlay">
                    <MarketLoadingIndicator
                      compact
                      message={loadingMessage ?? 'Загрузка предпросмотра…'}
                    />
                  </div>
                ) : null}
                <table className="market-table market-table--expandable market-table--configurable">
                  <colgroup>
                    {columnPrefs.visibleColumns.map((column) => (
                      <col
                        key={column.id}
                        style={
                          columnPrefs.getWidth(column.id) != null
                            ? { width: columnPrefs.getWidth(column.id) }
                            : undefined
                        }
                      />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {columnPrefs.visibleColumns.map((column) => (
                        <ResizableTh
                          key={column.id}
                          columnId={column.id}
                          label={column.label}
                          width={columnPrefs.getWidth(column.id)}
                          minWidth={columnPrefs.getMinWidth(column.id)}
                          onResize={columnPrefs.setWidth}
                        >
                          {column.id === 'actions' ? null : column.label}
                        </ResizableTh>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, index) => {
                      const fieldOverride = rowFieldOverrides[row.rowNumber]
                      const displayName = fieldOverride?.name ?? row.name
                      const displayBrand = fieldOverride?.brand ?? row.brand
                      const displayCountry = fieldOverride?.country ?? row.country
                      const displayPrice = fieldOverride?.price ?? row.price
                      const expanded = expandedRow === row.rowNumber
                      const selectedTypeId = effectiveImportRowTypeId(row, rowTypeOverrides)
                      const excluded = excludedRowNumbers.has(row.rowNumber)
                      const stripeClass = index % 2 === 0 ? 'market-table__row--odd' : 'market-table__row--even'
                      const matchingChangeClass =
                        highlightMatchingChanges && row.hasMatchingChange
                          ? 'market-table__row--matching-change'
                          : undefined

                      return (
                        <Fragment key={row.rowNumber}>
                          <tr
                            className={[
                              stripeClass,
                              expanded ? 'market-table__row--expanded' : undefined,
                              excluded ? 'market-table__row--excluded' : undefined,
                              matchingChangeClass,
                            ]
                              .filter(Boolean)
                              .join(' ') || undefined}
                            title={
                              highlightMatchingChanges && row.hasMatchingChange
                                ? 'Есть правки кроме цены'
                                : undefined
                            }
                          >
                            {columnPrefs.visibleColumns.map((column) => {
                              switch (column.id) {
                                case 'row':
                                  return <td key={column.id}>{row.rowNumber}</td>
                                case 'name':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.name ? 'market-table__cell--edited' : undefined}
                                    >
                                      {displayName}
                                    </td>
                                  )
                                case 'price':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.price ? 'market-table__cell--edited' : undefined}
                                    >
                                      {displayPrice ?? '—'}
                                    </td>
                                  )
                                case 'unit':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.unit ? 'market-table__cell--edited' : undefined}
                                    >
                                      {(fieldOverride?.unit ?? row.unit) ?? '—'}
                                    </td>
                                  )
                                case 'brand':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.brand ? 'market-table__cell--edited' : undefined}
                                    >
                                      {displayBrand ?? '—'}
                                    </td>
                                  )
                                case 'country':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.country ? 'market-table__cell--edited' : undefined}
                                    >
                                      {displayCountry ?? '—'}
                                    </td>
                                  )
                                case 'type':
                                  return (
                                    <td key={column.id}>
                                      {importRowTypeLabel(row, rowTypeOverrides, typeNames)}
                                    </td>
                                  )
                                case 'status':
                                  return (
                                    <td key={column.id}>
                                      {importMatchDecisionLabel(row.matchPreview.decision)}
                                    </td>
                                  )
                                case 'actions':
                                  return (
                                    <td key={column.id}>
                                      <div className="market-table__row-actions">
                                        <button
                                          type="button"
                                          className={`echat-btn echat-btn--ghost echat-btn--sm market-table__icon-btn${excluded ? '' : ' market-table__icon-btn--danger'}`}
                                          aria-label={excluded ? `Вернуть строку ${row.rowNumber}` : `Исключить строку ${row.rowNumber}`}
                                          title={excluded ? 'Вернуть' : 'Исключить'}
                                          disabled={busy}
                                          onClick={() => handleToggleExcludedRow(row.rowNumber)}
                                        >
                                          {excluded ? <IconUndo /> : <IconTrash />}
                                        </button>
                                        <button
                                          type="button"
                                          className="echat-btn echat-btn--ghost echat-btn--sm market-table__icon-btn"
                                          aria-label={
                                            expanded
                                              ? `Свернуть строку ${row.rowNumber}`
                                              : `Изменить строку ${row.rowNumber}`
                                          }
                                          title={expanded ? 'Свернуть' : 'Изменить'}
                                          disabled={busy}
                                          onClick={() => setExpandedRow(expanded ? null : row.rowNumber)}
                                        >
                                          <IconPencil />
                                        </button>
                                      </div>
                                    </td>
                                  )
                                default:
                                  return null
                              }
                            })}
                          </tr>
                          {expanded ? (
                            <tr className="market-table__edit-row">
                              <td colSpan={columnPrefs.visibleCount}>
                                <ImportRowEditPanel
                                  row={row}
                                  selectedProductTypeId={selectedTypeId}
                                  typePickedByOperator={rowTypeOverrides[row.rowNumber] != null}
                                  fieldOverride={fieldOverride ?? null}
                                  productTypes={productTypes}
                                  brandOptions={brandOptions}
                                  countryOptions={countryOptions}
                                  onTypeChange={(typeId) => {
                                    // Persist only explicit picks (including top candidate).
                                    // Confirm sends these as overrides; unedited rows are not
                                    // pinned from preview so reimport can preserve existing types.
                                    handleRowTypeOverride(row.rowNumber, typeId)
                                  }}
                                  onFieldOverrideChange={(override) =>
                                    handleRowFieldOverride(row.rowNumber, override)
                                  }
                                />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <MarketEmptyState
                title="Нет строк для предпросмотра"
                description="Укажите столбец «Наименование» в маппинге и обновите предпросмотр."
              />
            )}
            {previewDisplayTotalRows > PREVIEW_PAGE_SIZE ? (
              <div className="market-pagination">
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={busy || previewStale || previewPage <= 0}
                  onClick={() => void handlePreviewPageChange(previewPage - 1)}
                >
                  ←
                </button>
                <div className="market-pagination__jump">
                  Страница
                  <PreviewPageJump
                    page={previewPage}
                    totalPages={previewTotalPages}
                    disabled={busy || previewStale}
                    onChange={(nextPage) => void handlePreviewPageChange(nextPage)}
                  />
                  из {previewTotalPages}
                </div>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={busy || previewStale || previewPage >= previewTotalPages - 1}
                  onClick={() => void handlePreviewPageChange(previewPage + 1)}
                >
                  →
                </button>
              </div>
            ) : null}
          </article>

          <div className="market-inline-actions market-import-actions--sticky">
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={busy}
              onClick={() => setWizardStep('mapping')}
            >
              ← Назад к столбцам
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--primary"
              disabled={busy || previewStale || isPreviewSessionExpired(preview)}
              onClick={() => {
                if (isRestaurantMyPricesImport) {
                  openSupplierConfirm('confirm-import')
                  return
                }
                void handleConfirm()
              }}
            >
              {highlightMatchingChanges
                ? excludedRowNumbers.size > 0
                  ? `Подтвердить изменения (${preview.totalRows - excludedRowNumbers.size} из ${preview.totalRows})`
                  : 'Подтвердить изменения'
                : excludedRowNumbers.size > 0
                  ? `Импортировать (${preview.totalRows - excludedRowNumbers.size} из ${preview.totalRows})`
                  : 'Импортировать в каталог'}
            </button>
            {highlightMatchingChanges ? (
              <button
                type="button"
                className="echat-btn echat-btn--ghost"
                disabled={busy || previewStale || isPreviewSessionExpired(preview)}
                onClick={() => {
                  if (isRestaurantMyPricesImport) {
                    openSupplierConfirm('confirm-price-only')
                    return
                  }
                  void handleConfirm(undefined, 'price_only')
                }}
              >
                Обновить только цены
              </button>
            ) : null}
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={busy || savingDraft || previewStale || isPreviewSessionExpired(preview)}
              onClick={() => void handleSaveDraft()}
            >
              {savingDraft ? 'Сохраняем…' : 'Сохранить черновик'}
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              Другой файл
            </button>
            {draftSavedAt ? (
              <span className="echat-muted">Сохранено {formatDraftExpiry(draftSavedAt)}</span>
            ) : null}
          </div>
            </>
          )}
        </>
      ) : null}

      <ImportSupplierConfirmModal
        open={supplierConfirmAction != null}
        targetLabel={resolveImportSupplierLabel()}
        privateSuppliers={privateSuppliers}
        currentPrivateSupplierId={supplierMode === 'existing' ? selectedPrivateSupplierId : null}
        onDismiss={() => setSupplierConfirmAction(null)}
        onConfirm={handleSupplierConfirmAccepted}
      />
    </section>
  )
}
